/**
 * AES-256-GCM + ECDH crypto for Seismic shielded reads.
 *
 * Ported verbatim from the PoC at `packaged/safe-app-src20/src/seismic/crypto.ts`.
 * Matches coincurve / libsecp256k1 ECDH (SHA256 of compressed shared point) and
 * the Python PoC's HKDF-SHA256(info="aes-gcm key", L=32) — byte-for-byte.
 */
import { secp256k1 } from '@noble/curves/secp256k1'
import { sha256 } from '@noble/hashes/sha256'
import { hkdf } from '@noble/hashes/hkdf'
import { hexToBytes, bytesToHex, type Hex } from 'viem'

/**
 * ECDH shared secret matching coincurve / libsecp256k1's default behavior:
 * SHA256(compressed_point(client_sk * network_pk)). Not just the X-coordinate —
 * the entire 33-byte compressed point is hashed.
 */
export function ecdhSharedSecret(clientSk32: Uint8Array, networkPk33: Uint8Array): Uint8Array {
  const compressed = secp256k1.getSharedSecret(clientSk32, networkPk33, true)
  return sha256(compressed)
}

/**
 * Post-hardfork the node encrypts its response with a domain-separated key
 * (seismic-viem 3.0.1 `AesKeyDomain.TxResponse`); requests keep the old info.
 */
export const REQUEST_KEY_HKDF_INFO = 'aes-gcm key'
export const RESPONSE_KEY_HKDF_INFO = 'seismic/response/aes-256-gcm/v1'

/** HKDF-SHA256(ikm=shared, salt=null, info=<domain>, L=32). */
export function deriveAesKey(sharedSecret: Uint8Array, info: string = REQUEST_KEY_HKDF_INFO): Uint8Array {
  return hkdf(sha256, sharedSecret, undefined, info, 32)
}

async function importAesKey(key32: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', key32 as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

function ivFromNonce(encryptionNonce: bigint): Uint8Array {
  const iv = new Uint8Array(12)
  let n = encryptionNonce
  for (let i = 11; i >= 0; i--) {
    iv[i] = Number(n & 0xffn)
    n >>= 8n
  }
  return iv
}

/**
 * AES-256-GCM encrypt(plaintext, AAD). WebCrypto returns ciphertext || 16-byte
 * tag concatenated, matching the aes_gcm Rust crate output.
 */
export async function encryptCalldata(args: {
  plaintext: Uint8Array
  networkPk33: Uint8Array
  clientSk32: Uint8Array
  encryptionNonce: bigint
  aad: Uint8Array
}): Promise<Hex> {
  const shared = ecdhSharedSecret(args.clientSk32, args.networkPk33)
  const aesKey = deriveAesKey(shared)
  const iv = ivFromNonce(args.encryptionNonce)
  const cryptoKey = await importAesKey(aesKey)
  const buf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: args.aad as BufferSource, tagLength: 128 },
    cryptoKey,
    args.plaintext as BufferSource,
  )
  return bytesToHex(new Uint8Array(buf))
}

/** AES-256-GCM decrypt(ciphertext, AAD). Returns plaintext bytes — caller ABI-decodes. */
export async function decryptResponse(args: {
  ciphertextHex: Hex
  networkPk33: Uint8Array
  clientSk32: Uint8Array
  encryptionNonce: bigint
  aad: Uint8Array
}): Promise<Uint8Array> {
  const shared = ecdhSharedSecret(args.clientSk32, args.networkPk33)
  const aesKey = deriveAesKey(shared, RESPONSE_KEY_HKDF_INFO)
  const iv = ivFromNonce(args.encryptionNonce)
  const cryptoKey = await importAesKey(aesKey)
  const buf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: args.aad as BufferSource, tagLength: 128 },
    cryptoKey,
    hexToBytes(args.ciphertextHex) as BufferSource,
  )
  return new Uint8Array(buf)
}

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n)
  crypto.getRandomValues(b)
  return b
}

/** Fresh secp256k1 keypair — used per-call as the ECDH ephemeral. */
export function randomKeypair(): { sk: Uint8Array; pk33: Uint8Array } {
  let sk: Uint8Array
  do {
    sk = randomBytes(32)
  } while (!secp256k1.utils.isValidPrivateKey(sk))
  const pk33 = secp256k1.getPublicKey(sk, true)
  return { sk, pk33 }
}
